import axios from 'axios';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '@/context/appContext';
import { queueItemPath } from '@/utils/queueItemRoute';

import { CombinedQueueItem, useCombinedQueue } from './useCombinedQueue';

export const useCombinedGetNextQueue = () => {
  const navigate = useNavigate();
  const { authInfo } = useAuth();
  const queue = useCombinedQueue();
  const [pending, setPending] = useState(false);

  const getNextQueue = useCallback(
    async (opts: { source: string; medium?: string }) => {
      if (!authInfo || pending) {
        return;
      }
      setPending(true);
      const params = { utm_source: opts.source, utm_medium: opts.medium };
      const open = (item: CombinedQueueItem) =>
        navigate(queueItemPath(item.id, item.region, params), {
          state: { queueItem: item, region: item.region },
        });
      try {
        for (const item of queue.items.filter(candidate => !candidate.first_reviewer_user_id)) {
          const apis = queue.regions[item.region]?.apis;
          if (!apis) {
            continue;
          }
          try {
            const res = await apis.dataReviewApi.appApiDataReviewerPostDataReviewerClaimQueue({
              data_reviewer_id: authInfo.reviewer_id,
              meal_photo_queue_id: item.id,
            });
            if (res.data.ok) {
              queue.refetch('get-next');
              open({
                ...item,
                first_reviewer_user_id: authInfo.reviewer_id,
                first_reviewer_access_time: new Date().toISOString(),
              });
              return;
            }
          } catch (error) {
            // A concurrent claim can make the row stale. Try the next oldest row.
            if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) {
              throw error;
            }
          }
        }
        const owned = queue.items.find(item => item.first_reviewer_user_id === authInfo.reviewer_id);
        if (owned) {
          open(owned);
          return;
        }
        queue.refetch('get-next');
      } finally {
        setPending(false);
      }
    },
    [authInfo, navigate, pending, queue],
  );

  return { getNextQueue, pending };
};
